import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getGeoLocation, submitLead, redirectToApplication } from '../services/api';
import './HeroSection.css';

const HeroSection = ({ 
  eyebrow, 
  title, 
  description, 
  ctaText, 
  ctaLink, 
  backgroundImage,
  showForm = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('us');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('1');

  useEffect(() => {
    // Fetch geolocation on component mount
    const fetchGeoData = async () => {
      const geoData = await getGeoLocation();
      if (geoData && geoData.data) {
        setCountry(geoData.data.countryName || '');
        const code = (geoData.data.countryCode || 'US').toLowerCase();
        setCountryCode(code);
      }
    };
    if (showForm) {
      fetchGeoData();
    }
  }, [showForm]);

  const handlePhoneChange = (value, country) => {
    setPhone(value);
    setDialCode(country.dialCode);
    setCountryCode(country.countryCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formData = new FormData(e.target);
    
    // Extract only numeric phone number (remove country code)
    const phoneNumber = phone.replace(/\D/g, '').replace(dialCode, '');

    const leadData = {
      name: formData.get('name'),
      email: formData.get('email'),
      phoneNumber: phoneNumber,
      countryDialCode: parseInt(dialCode),
      country: countryCode.toUpperCase(),
      description: formData.get('description'),
      packageTitle: 'Inquiry',
      packageAmount: 0
    };

    const result = await submitLead(leadData);

    if (result.success) {
      // Redirect to application page
      redirectToApplication(result.customerId);
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <section className="hero-section" style={{ backgroundImage: `url(${backgroundImage})` }}>
      <div className="hero-overlay"></div>
      <div className="hero-content">
        <div className="hero-text">
          {eyebrow && <p className="hero-eyebrow">{eyebrow}</p>}
          <h1 className="hero-title">{title}</h1>
          <p className="hero-description">{description}</p>
          {!showForm && ctaText && (
            <a href={ctaLink || '#contact'}>
              <Button size="lg" className="hero-cta">{ctaText}</Button>
            </a>
          )}
        </div>
        
        {showForm && (
          <div className="hero-form-wrapper">
            <div className="hero-form-card">
              <div className="form-header-section">
                <h3 className="form-title">Get Started Today</h3>
                <p className="form-subtitle">Complete the form below and we'll contact you shortly</p>
              </div>
              
              {error && (
                <div className="form-error">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="hero-form">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Enter your full name" 
                    required 
                    className="form-input"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="your.email@example.com" 
                    required 
                    className="form-input"
                    disabled={loading}
                    pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                    title="Please enter a valid email address"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Contact Number (Country Code + 10 Digits) *</label>
                  <PhoneInput
                    country={countryCode}
                    value={phone}
                    onChange={handlePhoneChange}
                    disabled={loading}
                    inputProps={{
                      name: 'phone',
                      required: true,
                      placeholder: 'Country code + 10 digits',
                      minLength: 10,
                      maxLength: 15
                    }}
                    containerClass="phone-input-container"
                    inputClass="phone-input-field"
                    buttonClass="phone-input-button"
                    dropdownClass="phone-input-dropdown"
                    enableSearch={true}
                    disableSearchIcon={false}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <textarea 
                    name="description" 
                    placeholder="Tell us about your trademark needs..." 
                    required 
                    className="form-textarea"
                    disabled={loading}
                    rows="3"
                  />
                </div>

                <Button type="submit" size="lg" className="form-submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Inquiry</span>
                      <span className="arrow-icon">→</span>
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;